import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Search, ChevronLeft, Filter, Tag, Archive, Star,
  Mail, Trash2, AlertOctagon, Bookmark, BookmarkX, Forward
} from 'lucide-react'
import { useUIStore } from '../../stores/uiStore'
import { useMailStore } from '../../stores/mailStore'

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilterCriteria {
  from: string
  to: string
  subject: string
  hasWords: string
  doesntHave: string
  sizeOp: 'greater_than' | 'less_than'
  sizeValue: string
  sizeUnit: 'MB' | 'KB' | 'bytes'
  hasAttachment: boolean
}

interface FilterActions {
  skipInbox: boolean
  markRead: boolean
  star: boolean
  applyLabelId: string
  forwardTo: string
  delete: boolean
  neverSpam: boolean
  alwaysImportant: boolean
  neverImportant: boolean
}

const defaultCriteria = (): FilterCriteria => ({
  from: '', to: '', subject: '',
  hasWords: '', doesntHave: '',
  sizeOp: 'greater_than', sizeValue: '', sizeUnit: 'MB',
  hasAttachment: false,
})

const defaultActions = (): FilterActions => ({
  skipInbox: false, markRead: false, star: false,
  applyLabelId: '', forwardTo: '', delete: false,
  neverSpam: false, alwaysImportant: false, neverImportant: false,
})

// ─── Step 1: Criteria ─────────────────────────────────────────────────────────

function CriteriaForm({ criteria, onChange, onSearch, onNext, onClose }: {
  criteria: FilterCriteria
  onChange: (patch: Partial<FilterCriteria>) => void
  onSearch: () => void
  onNext: () => void
  onClose: () => void
}) {
  const hasAnyCriteria = criteria.from || criteria.to || criteria.subject ||
    criteria.hasWords || criteria.doesntHave || criteria.hasAttachment ||
    (criteria.sizeValue && criteria.sizeOp)

  return (
    <div className="fcm__body">
      <div className="fcm__field-grid">
        {([
          ['From', 'from', 'text'],
          ['To', 'to', 'text'],
          ['Subject', 'subject', 'text'],
          ['Has the words', 'hasWords', 'text'],
          ["Doesn't have", 'doesntHave', 'text'],
        ] as const).map(([label, field, type]) => (
          <div key={field} className="fcm__field-row">
            <label className="fcm__label">{label}</label>
            <input
              type={type}
              className="fcm__input"
              value={criteria[field] as string}
              onChange={(e) => onChange({ [field]: e.target.value })}
              autoFocus={field === 'from'}
            />
          </div>
        ))}

        {/* Size row */}
        <div className="fcm__field-row">
          <label className="fcm__label">Size</label>
          <div className="fcm__size-row">
            <select
              className="fcm__select"
              value={criteria.sizeOp}
              onChange={(e) => onChange({ sizeOp: e.target.value as FilterCriteria['sizeOp'] })}
            >
              <option value="greater_than">greater than</option>
              <option value="less_than">less than</option>
            </select>
            <input
              type="number"
              className="fcm__input fcm__size-value"
              value={criteria.sizeValue}
              onChange={(e) => onChange({ sizeValue: e.target.value })}
              placeholder="0"
              min="0"
            />
            <select
              className="fcm__select"
              value={criteria.sizeUnit}
              onChange={(e) => onChange({ sizeUnit: e.target.value as FilterCriteria['sizeUnit'] })}
            >
              <option value="MB">MB</option>
              <option value="KB">KB</option>
              <option value="bytes">bytes</option>
            </select>
          </div>
        </div>

        {/* Has attachment */}
        <div className="fcm__field-row fcm__field-row--check">
          <label className="fcm__label" />
          <label className="fcm__checkbox-label">
            <input
              type="checkbox"
              checked={criteria.hasAttachment}
              onChange={(e) => onChange({ hasAttachment: e.target.checked })}
            />
            <span>Has attachment</span>
          </label>
        </div>
      </div>

      <div className="fcm__footer">
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onNext}
            disabled={!hasAnyCriteria}
          >
            <Filter size={13} /> Create filter
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={onSearch}
            disabled={!hasAnyCriteria}
          >
            <Search size={13} /> Search
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Step 2: Actions ──────────────────────────────────────────────────────────

function ActionsForm({ actions, criteria, onChange, onBack, onCreate, onClose }: {
  actions: FilterActions
  criteria: FilterCriteria
  onChange: (patch: Partial<FilterActions>) => void
  onBack: () => void
  onCreate: () => void
  onClose: () => void
}) {
  const { labels } = useMailStore()
  const userLabels = labels.filter((l) => !l.is_system)

  const hasAnyAction = actions.skipInbox || actions.markRead || actions.star ||
    actions.applyLabelId || actions.forwardTo || actions.delete ||
    actions.neverSpam || actions.alwaysImportant || actions.neverImportant

  const CheckRow = ({ field, label, icon: Icon }: {
    field: keyof FilterActions
    label: string
    icon: React.ElementType
  }) => (
    <label className="fcm__action-row">
      <input
        type="checkbox"
        checked={!!actions[field]}
        onChange={(e) => onChange({ [field]: e.target.checked })}
      />
      <Icon size={14} className="fcm__action-icon" />
      <span>{label}</span>
    </label>
  )

  return (
    <div className="fcm__body">
      <div className="fcm__criteria-summary">
        <span className="fcm__criteria-label">Matches:</span>
        <span className="fcm__criteria-value">
          {[
            criteria.from && `from:(${criteria.from})`,
            criteria.to && `to:(${criteria.to})`,
            criteria.subject && `subject:(${criteria.subject})`,
            criteria.hasWords && `"${criteria.hasWords}"`,
          ].filter(Boolean).join(' ')}
        </span>
      </div>

      <p className="fcm__actions-title">When a message matches this filter, do the following:</p>

      <div className="fcm__actions-list">
        <CheckRow field="skipInbox"        label="Skip the Inbox (Archive it)"   icon={Archive} />
        <CheckRow field="markRead"         label="Mark as read"                  icon={Mail} />
        <CheckRow field="star"             label="Star it"                        icon={Star} />
        <CheckRow field="delete"           label="Delete it"                      icon={Trash2} />
        <CheckRow field="neverSpam"        label="Never send it to Spam"          icon={AlertOctagon} />
        <CheckRow field="alwaysImportant"  label="Always mark as important"       icon={Bookmark} />
        <CheckRow field="neverImportant"   label="Never mark as important"        icon={BookmarkX} />

        {/* Apply label */}
        <label className="fcm__action-row">
          <input
            type="checkbox"
            checked={!!actions.applyLabelId}
            onChange={(e) => !e.target.checked && onChange({ applyLabelId: '' })}
          />
          <Tag size={14} className="fcm__action-icon" />
          <span>Apply the label:</span>
          <select
            className="fcm__select fcm__select--inline"
            value={actions.applyLabelId}
            onChange={(e) => onChange({ applyLabelId: e.target.value })}
          >
            <option value="">Choose label</option>
            {userLabels.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </label>

        {/* Forward to */}
        <label className="fcm__action-row">
          <input
            type="checkbox"
            checked={!!actions.forwardTo}
            onChange={(e) => !e.target.checked && onChange({ forwardTo: '' })}
          />
          <Forward size={14} className="fcm__action-icon" />
          <span>Forward it to:</span>
          <input
            type="email"
            className="fcm__input fcm__input--inline"
            placeholder="email@example.com"
            value={actions.forwardTo}
            onChange={(e) => onChange({ forwardTo: e.target.value })}
          />
        </label>
      </div>

      <div className="fcm__footer">
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onBack}>
            <ChevronLeft size={13} /> Back
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={onCreate}
            disabled={!hasAnyAction}
          >
            Create filter
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal shell ──────────────────────────────────────────────────────────────

export function FilterCreatorModal() {
  const { filterCreatorOpen, filterCreatorDefaults, closeFilterCreator } = useUIStore()
  const { setSearchQuery } = useMailStore()
  const { addToast } = useUIStore()

  const [step, setStep] = useState<1 | 2>(1)
  const [criteria, setCriteria] = useState<FilterCriteria>(defaultCriteria)
  const [actions, setActions] = useState<FilterActions>(defaultActions)

  // Pre-populate criteria from defaults whenever the modal opens
  useEffect(() => {
    if (filterCreatorOpen) {
      setStep(1)
      setCriteria({ ...defaultCriteria(), ...filterCreatorDefaults })
      setActions(defaultActions())
    }
  }, [filterCreatorOpen, filterCreatorDefaults])

  if (!filterCreatorOpen) return null

  const handleSearch = () => {
    const parts: string[] = []
    if (criteria.from) parts.push(`from:(${criteria.from})`)
    if (criteria.to) parts.push(`to:(${criteria.to})`)
    if (criteria.subject) parts.push(`subject:(${criteria.subject})`)
    if (criteria.hasWords) parts.push(criteria.hasWords)
    setSearchQuery(parts.join(' '))
    closeFilterCreator()
  }

  const handleCreate = () => {
    // Build conditions array for API
    const conditions = [
      criteria.from && { field: 'from', operator: 'contains', value: criteria.from },
      criteria.to && { field: 'to', operator: 'contains', value: criteria.to },
      criteria.subject && { field: 'subject', operator: 'contains', value: criteria.subject },
      criteria.hasWords && { field: 'body', operator: 'contains', value: criteria.hasWords },
      criteria.doesntHave && { field: 'body', operator: 'not_contains', value: criteria.doesntHave },
      criteria.hasAttachment && { field: 'has_attachment', operator: 'is', value: 'true' },
      criteria.sizeValue && { field: 'size', operator: criteria.sizeOp, value: `${criteria.sizeValue}${criteria.sizeUnit}` },
    ].filter(Boolean)

    const filterActions = [
      actions.skipInbox && { type: 'skip_inbox' },
      actions.markRead && { type: 'mark_read' },
      actions.star && { type: 'star' },
      actions.delete && { type: 'delete' },
      actions.neverSpam && { type: 'never_spam' },
      actions.alwaysImportant && { type: 'always_important' },
      actions.neverImportant && { type: 'never_important' },
      actions.applyLabelId && { type: 'apply_label', value: actions.applyLabelId },
      actions.forwardTo && { type: 'forward_to', value: actions.forwardTo },
    ].filter(Boolean)

    console.log('Creating filter:', { conditions, actions: filterActions })
    // TODO: POST /api/filters with { name, conditions, actions }

    addToast('Filter created', 'success')
    closeFilterCreator()
  }

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fcm-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={closeFilterCreator}
      />

      {/* Modal */}
      <motion.div
        className="fcm"
        initial={{ opacity: 0, y: -8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Header */}
        <div className="fcm__header">
          <div className="fcm__header-left">
            <Filter size={15} style={{ color: 'var(--brand)' }} />
            <span className="fcm__title">
              {step === 1 ? 'Create filter' : 'Filter actions'}
            </span>
            <div className="fcm__steps">
              <span className={`fcm__step${step === 1 ? ' fcm__step--active' : ''}`}>1</span>
              <span className="fcm__step-line" />
              <span className={`fcm__step${step === 2 ? ' fcm__step--active' : ''}`}>2</span>
            </div>
          </div>
          <button className="btn-icon" onClick={closeFilterCreator} aria-label="Close">
            <X size={15} />
          </button>
        </div>

        {/* Body — step 1 or 2 */}
        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div key="step1" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.14 }}>
              <CriteriaForm
                criteria={criteria}
                onChange={(p) => setCriteria((prev) => ({ ...prev, ...p }))}
                onSearch={handleSearch}
                onNext={() => setStep(2)}
                onClose={closeFilterCreator}
              />
            </motion.div>
          ) : (
            <motion.div key="step2" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} transition={{ duration: 0.14 }}>
              <ActionsForm
                actions={actions}
                criteria={criteria}
                onChange={(p) => setActions((prev) => ({ ...prev, ...p }))}
                onBack={() => setStep(1)}
                onCreate={handleCreate}
                onClose={closeFilterCreator}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <style>{`
        .fcm-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.35);
          z-index: 1400;
        }

        .fcm {
          position: fixed;
          top: 64px;
          left: 50%;
          transform: translateX(-50%);
          width: 580px;
          max-width: calc(100vw - 32px);
          background: var(--bg-surface);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-xl);
          z-index: 1500;
          overflow: hidden;
        }

        .fcm__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          border-bottom: 1px solid var(--border);
          background: var(--bg-elevated);
        }

        .fcm__header-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .fcm__title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .fcm__steps {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-left: 8px;
        }

        .fcm__step {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 1.5px solid var(--border-strong);
          color: var(--text-tertiary);
          font-size: 11px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .fcm__step--active {
          border-color: var(--brand);
          background: var(--brand);
          color: white;
        }

        .fcm__step-line {
          width: 16px;
          height: 1.5px;
          background: var(--border-strong);
        }

        .fcm__body {
          padding: 0;
        }

        .fcm__field-grid {
          padding: 8px 0;
        }

        .fcm__field-row {
          display: flex;
          align-items: center;
          padding: 6px 18px;
          border-bottom: 1px solid var(--border);
          min-height: 44px;
        }

        .fcm__label {
          width: 120px;
          flex-shrink: 0;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .fcm__input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          font-family: var(--font-ui);
          font-size: 13px;
          color: var(--text-primary);
          padding: 4px 0;
        }
        .fcm__input::placeholder { color: var(--text-tertiary); }

        .fcm__input--inline {
          flex: 1;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          padding: 3px 8px;
          margin-left: 4px;
        }

        .fcm__size-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
        }

        .fcm__size-value {
          width: 80px;
          flex: none;
          text-align: center;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          padding: 3px 8px;
        }

        .fcm__select {
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          padding: 4px 8px;
          font-family: var(--font-ui);
          font-size: 12px;
          color: var(--text-primary);
          outline: none;
          cursor: pointer;
        }

        .fcm__select--inline {
          flex: 1;
          margin-left: 4px;
        }

        .fcm__field-row--check {
          border-bottom: none;
        }

        .fcm__checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .fcm__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 18px;
          border-top: 1px solid var(--border);
          background: var(--bg-elevated);
        }

        /* Step 2 */
        .fcm__criteria-summary {
          display: flex;
          align-items: baseline;
          gap: 8px;
          padding: 10px 18px;
          background: var(--bg-elevated);
          border-bottom: 1px solid var(--border);
          font-size: 12px;
        }

        .fcm__criteria-label {
          color: var(--text-tertiary);
          font-weight: 600;
          flex-shrink: 0;
        }

        .fcm__criteria-value {
          color: var(--text-secondary);
          font-family: var(--font-mono);
          word-break: break-all;
        }

        .fcm__actions-title {
          padding: 12px 18px 8px;
          font-size: 12px;
          color: var(--text-tertiary);
          font-style: italic;
        }

        .fcm__actions-list {
          padding: 0 18px 8px;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .fcm__action-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          border-radius: var(--radius-sm);
          font-size: 13px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: background var(--duration-fast);
        }
        .fcm__action-row:hover { background: var(--bg-hover); color: var(--text-primary); }
        .fcm__action-row input[type="checkbox"] { cursor: pointer; accent-color: var(--brand); }

        .fcm__action-icon {
          color: var(--text-tertiary);
          flex-shrink: 0;
        }
      `}</style>
    </>
  )
}
